/* ==========================================================================
   tire-size-search.js — "I know my size, show me every tire in it."
   Taylor's idea (2026-10-12): riders read the size off the sidewall but
   don't know what we carry in it. No fitment data needed — the size IS the
   fitment, and it already says front or rear.

   The size is a DROPDOWN of what we carry in stock (Adam, 2026-10-12), one
   option per width / aspect / rim / construction, grouped by rim. Spellings
   that are the same tire size merge into one option (130/90B16 + 130/90B16
   BLK), labelled with the shortest one.

   A typed size still arrives by link (?size=180/55R17), so the parser stays:
   180/55R17, 180 55 17 and 1805517 all land on 180/55ZR17 (ZR and R are the
   same size; Z is a speed rating). Radial vs bias IS a real difference: a
   link asking for one we only carry in the other shows it with a warning.

   Segment = the product's riding-style tag (Track, Sport, Touring...), the
   same tags behind the "Shop by Riding Style" collections.

   Markup contract — any element with [data-tire-size-search]:
     data-collection="tires"          collection handle to read
     data-segments="Track,Sport,..."  tags that count as a segment, in order
     data-phone="(317) 207-0525"      shown when a linked size isn't listed
   Inside it:
     [data-tss-form] [data-tss-size] [data-tss-segment]
     [data-tss-popular] [data-tss-status] [data-tss-results]
   URL: ?size=180/55ZR17&ride=Sport opens with that search (link-shareable).
   ========================================================================== */
(function () {
  'use strict';

  var PAGE_SIZE = 250;
  var RADIAL = 'radial';
  var BIAS = 'bias';

  function constructionClass(mark, fromShopper) {
    if (mark === 'ZR' || mark === 'VR' || mark === 'R') return RADIAL;
    if (mark === 'B') return BIAS;
    // Our catalog spells bias as "130/70-18"; a shopper typing a dash is
    // usually just separating numbers, so from them it means "don't know".
    if (mark === '-') return fromShopper ? null : BIAS;
    return null;
  }

  // -> { key: '180/55', rim: '17' | null, cls: 'radial' | 'bias' | null, mark }
  function parseSize(raw, fromShopper) {
    var s = String(raw || '').toUpperCase()
      .replace(/M\/C|\/MC\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    var m =
      // metric: 180/55ZR17, 180 55 17, 1805517, 90/90-19
      s.match(/^(\d{2,3})\s*[\/ \-]?\s*(\d{2})(?:\s*(ZR|VR|R|B|-)?\s*-?\s*(\d{2}))?(?!\d)/) ||
      // alpha: MT90B16, MH90-21, MU85B16
      s.match(/^(M[A-Z])\s*(\d{2})(?:\s*(R|B|-)?\s*-?\s*(\d{2}))?(?!\d)/) ||
      // inch: 4.00-18
      s.match(/^(\d{1,2}\.\d{2})()(?:\s*(R|B|-)?\s*-?\s*(\d{2}))?(?!\d)/);
    if (!m) return null;
    return {
      key: m[2] ? m[1] + '/' + m[2] : m[1],
      rim: m[4] || null,
      cls: constructionClass(m[3] || '', fromShopper),
      mark: m[3] || ''
    };
  }

  function money(price) {
    var n = Number(price);
    return isNaN(n) ? '' : '$' + n.toFixed(2);
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  function thumb(src) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=160';
  }

  function fetchAll(handle) {
    var all = [];
    function page(n) {
      return fetch('/collections/' + encodeURIComponent(handle) +
                   '/products.json?limit=' + PAGE_SIZE + '&page=' + n)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          var batch = (data && data.products) || [];
          all = all.concat(batch);
          return batch.length === PAGE_SIZE && n < 20 ? page(n + 1) : all;
        });
    }
    return page(1);
  }

  // One row per in-stock variant.
  function buildRows(products, segments) {
    var rows = [];
    products.forEach(function (p) {
      var tags = p.tags || [];
      var segment = null;
      for (var i = 0; i < segments.length && !segment; i++) {
        if (tags.indexOf(segments[i]) !== -1) segment = segments[i];
      }
      var img = p.images && p.images[0] && p.images[0].src;
      (p.variants || []).forEach(function (v) {
        if (v.available === false) return;
        var size = (v.option1 || v.title || '').trim();
        var parsed = parseSize(size, false);
        if (!parsed || !parsed.rim) return;
        rows.push({
          brand: (p.vendor || '').trim(),
          model: (p.title || '').replace(/\s+Tire\b/i, '').trim(),
          segment: segment,
          size: size,
          parsed: parsed,
          price: Number(v.price),
          img: (v.featured_image && v.featured_image.src) || img,
          url: '/products/' + p.handle + '?variant=' + v.id
        });
      });
    });
    return rows;
  }

  function optionId(parsed) {
    return parsed.key + '|' + parsed.rim + '|' + (parsed.cls || '');
  }

  // One option per distinct size, grouped by rim. -> { byId, rims: { rim: [opt] } }
  function buildOptions(rows) {
    var byId = {};
    rows.forEach(function (r) {
      var id = optionId(r.parsed);
      var opt = byId[id] || (byId[id] = { id: id, parsed: r.parsed, label: r.size, count: 0 });
      if (r.size.length < opt.label.length) { opt.label = r.size; opt.parsed = r.parsed; }
      opt.count++;
    });
    var rims = {};
    Object.keys(byId).forEach(function (id) {
      var o = byId[id];
      (rims[o.parsed.rim] || (rims[o.parsed.rim] = [])).push(o);
    });
    Object.keys(rims).forEach(function (rim) {
      rims[rim].sort(function (a, b) {
        var wa = parseFloat(a.parsed.key), wb = parseFloat(b.parsed.key);
        if (isNaN(wa) !== isNaN(wb)) return isNaN(wa) ? 1 : -1;
        return (wa - wb) || (a.label < b.label ? -1 : 1);
      });
    });
    return { byId: byId, rims: rims };
  }

  function init(root) {
    if (root.hasAttribute('data-tss-ready')) return;
    root.setAttribute('data-tss-ready', '');

    var handle = root.getAttribute('data-collection') || 'tires';
    var segments = (root.getAttribute('data-segments') || 'Track,Sport,Touring,Cruiser')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    var phone = root.getAttribute('data-phone') || '';

    var form = root.querySelector('[data-tss-form]');
    var sizeSel = root.querySelector('[data-tss-size]');
    var segSel = root.querySelector('[data-tss-segment]');
    var popular = root.querySelector('[data-tss-popular]');
    var status = root.querySelector('[data-tss-status]');
    var results = root.querySelector('[data-tss-results]');
    if (!form || !sizeSel || !segSel || !status || !results) return;

    var rows = null;
    var options = null;
    var loading = null;

    function load() {
      if (!loading) {
        loading = fetchAll(handle).then(function (products) {
          rows = buildRows(products, segments);
          options = buildOptions(rows);
          fillSizes();
          fillSegments(null);
          return rows;
        });
        loading.catch(function (err) {
          if (window.console) console.error('[tire-size-search] load failed:', err);
          sizeSel.innerHTML = '';
          sizeSel.appendChild(el('option', null, 'Sizes unavailable'));
          say(['Tires are unavailable right now. Please try again in a minute.']);
        });
      }
      return loading;
    }

    function fillSizes() {
      sizeSel.innerHTML = '';
      var blank = el('option', null, 'Choose your size');
      blank.value = '';
      sizeSel.appendChild(blank);
      Object.keys(options.rims)
        .sort(function (a, b) { return Number(a) - Number(b); })
        .forEach(function (rim) {
          var group = document.createElement('optgroup');
          group.label = rim + '\u2033 wheel';
          options.rims[rim].forEach(function (o) {
            var opt = el('option', null, o.label);
            opt.value = o.id;
            group.appendChild(opt);
          });
          sizeSel.appendChild(group);
        });
      sizeSel.disabled = false;

      if (popular) {
        popular.innerHTML = '';
        Object.keys(options.byId)
          .map(function (id) { return options.byId[id]; })
          .sort(function (a, b) { return b.count - a.count || (a.label < b.label ? -1 : 1); })
          .slice(0, 5)
          .forEach(function (o) {
            var b = el('button', 'tss__chip', o.label);
            b.type = 'button';
            b.addEventListener('click', function () { sizeSel.value = o.id; run(true); });
            popular.appendChild(b);
          });
        popular.parentNode.hidden = !popular.childNodes.length;
      }
    }

    // Segment options; with a size chosen, counts for THIS size and empty ones disabled.
    function fillSegments(found) {
      var current = segSel.value;
      var counts = {};
      (found || []).forEach(function (r) { if (r.segment) counts[r.segment] = (counts[r.segment] || 0) + 1; });
      segSel.innerHTML = '';
      var all = el('option', null, 'All riding styles' + (found ? ' (' + found.length + ')' : ''));
      all.value = '';
      segSel.appendChild(all);
      segments.forEach(function (s) {
        if (!rows.some(function (r) { return r.segment === s; })) return;
        var o = el('option', null, s + (found ? ' (' + (counts[s] || 0) + ')' : ''));
        o.value = s;
        if (found && !counts[s]) o.disabled = true;
        segSel.appendChild(o);
      });
      segSel.value = current;
      if (segSel.selectedIndex < 0 || segSel.options[segSel.selectedIndex].disabled) segSel.value = '';
    }

    function card(r) {
      var a = el('a', 'tss__card');
      a.href = r.url;
      if (r.img) {
        var img = el('img', 'tss__img');
        img.src = thumb(r.img);
        img.alt = '';
        img.loading = 'lazy';
        img.width = 80;
        img.height = 80;
        a.appendChild(img);
      } else {
        a.appendChild(el('span', 'tss__img tss__img--blank'));
      }
      var body = el('span', 'tss__body');
      body.appendChild(el('span', 'tss__brand', r.brand));
      body.appendChild(el('span', 'tss__model', r.model));
      var meta = el('span', 'tss__meta');
      if (r.segment) meta.appendChild(el('span', 'tss__badge', r.segment));
      meta.appendChild(el('span', 'tss__size', r.size));
      body.appendChild(meta);
      a.appendChild(body);
      var right = el('span', 'tss__buy');
      right.appendChild(el('span', 'tss__price', money(r.price)));
      right.appendChild(el('span', 'tss__cta', 'View'));
      a.appendChild(right);
      return a;
    }

    function say(parts) {
      status.innerHTML = '';
      parts.forEach(function (p) {
        status.appendChild(typeof p === 'string' ? document.createTextNode(p) : p);
      });
      status.hidden = parts.length === 0;
    }

    function syncUrl() {
      if (!window.history || !history.replaceState) return;
      var opt = options && options.byId[sizeSel.value];
      var params = new URLSearchParams(location.search);
      if (opt) params.set('size', opt.label); else params.delete('size');
      if (opt && segSel.value) params.set('ride', segSel.value); else params.delete('ride');
      var q = params.toString();
      history.replaceState(null, '', location.pathname + (q ? '?' + q : '') + location.hash);
    }

    // notes: extra sentences from a linked size (ZR/R, construction swap).
    function render(notes) {
      var opt = options.byId[sizeSel.value];
      results.innerHTML = '';
      if (!opt) { fillSegments(null); say([]); return; }

      var found = rows.filter(function (r) { return optionId(r.parsed) === opt.id; });
      fillSegments(found);

      var segment = segSel.value;
      var shown = segment ? found.filter(function (r) { return r.segment === segment; }) : found;
      shown.sort(function (a, b) {
        var sa = segments.indexOf(a.segment), sb = segments.indexOf(b.segment);
        if (!segment && sa !== sb) return (sa < 0 ? 99 : sa) - (sb < 0 ? 99 : sb);
        return a.price - b.price;
      });

      var sizesShown = shown.map(function (r) { return r.size; })
        .filter(function (s, i, a) { return a.indexOf(s) === i; });
      say([shown.length + (shown.length === 1 ? ' tire' : ' tires') + ' in ' +
        (sizesShown.join(', ') || opt.label) + (segment ? ' \u00b7 ' + segment : '') + '. ']
        .concat(notes || []));

      var lastSeg;
      shown.forEach(function (r) {
        if (!segment && r.segment !== lastSeg) {
          results.appendChild(el('h3', 'tss__group', r.segment || 'Other'));
          lastSeg = r.segment;
        }
        results.appendChild(card(r));
      });
    }

    function run(fromUser, notes) {
      load().then(function () {
        render(notes);
        if (fromUser) syncUrl();
      });
    }

    // A typed size from a link -> the matching option, or a not-listed message.
    function openLinked(typed, ride) {
      var want = parseSize(typed, true);
      load().then(function () {
        if (ride) segSel.value = ride;
        if (!want || !want.rim) {
          say(['That link\u2019s size (' + typed + ') isn\u2019t one we recognise. Choose your size above.']);
          return;
        }
        var all = Object.keys(options.byId).map(function (id) { return options.byId[id]; })
          .filter(function (o) { return o.parsed.key === want.key && o.parsed.rim === want.rim; });
        var same = want.cls ? all.filter(function (o) { return o.parsed.cls === want.cls; }) : all;
        var pick = same[0] || all[0];
        if (!pick) {
          var msg = ['We don\u2019t list a tire in ' + typed.toUpperCase() + ' right now.'];
          if (phone) {
            var tel = el('a', null, phone);
            tel.href = 'tel:+1' + phone.replace(/\D/g, '').replace(/^1/, '');
            msg.push(' Call ', tel, ' and we\u2019ll find one for you.');
          }
          say(msg);
          return;
        }
        var notes = [];
        if (!same.length) {
          notes.push('We don\u2019t carry a ' + want.cls + ' tire in this size. These are ' +
            (want.cls === RADIAL ? BIAS : RADIAL) + '-ply \u2014 check your bike\u2019s spec before switching construction. ');
        } else if (want.mark === 'R' && pick.parsed.mark === 'ZR') {
          notes.push('ZR and R are the same size \u2014 the Z is a higher speed rating. ');
        }
        sizeSel.value = pick.id;
        render(notes);
      });
    }

    form.addEventListener('submit', function (e) { e.preventDefault(); run(true); });
    sizeSel.addEventListener('change', function () { run(true); });
    segSel.addEventListener('change', function () { if (sizeSel.value) run(true); });

    var params = new URLSearchParams(location.search);
    if (params.get('size')) {
      openLinked(params.get('size'), params.get('ride'));
    } else if ('IntersectionObserver' in window) {
      // Load the sizes once the widget is near the viewport.
      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) { io.disconnect(); load(); }
      }, { rootMargin: '200px' });
      io.observe(root);
    } else {
      load();
    }
  }

  function boot() {
    document.querySelectorAll('[data-tire-size-search]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
